/**
 * Exam microphone recorder for the clinic chart.
 *
 * Prefers a normal capture session (no voice-call processing) and AAC around
 * 128 kbps. If the browser rejects those constraints, it falls back to the
 * default mic and reports that so the clinician knows.
 */

import { onUnmounted, ref } from 'vue';

const MIME_CANDIDATES = [
  'audio/mp4',
  'audio/aac',
  'audio/webm;codecs=opus',
  'audio/webm',
];

const STRICT_AUDIO = {
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
  channelCount: 1,
  sampleRate: 48000,
};

function pickMime() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
  return MIME_CANDIDATES.find((mime) => MediaRecorder.isTypeSupported(mime)) || '';
}

const MAX_SEC = 45 * 60;

function extensionFor(mime) {
  if (mime.includes('mp4') || mime.includes('aac')) return 'm4a';
  if (mime.includes('webm')) return 'webm';
  return 'audio';
}

export function useExamRecorder() {
  const recording = ref(false);
  const elapsedSec = ref(0);
  const recorderError = ref('');
  const usedDefaultMic = ref(false);
  const capped = ref(false);

  let recorder = null;
  let chunks = [];
  let stream = null;
  let timer = null;
  let capTimer = null;
  let stopped = null;
  let pendingFile = null;

  async function openStream() {
    usedDefaultMic.value = false;
    try {
      return await navigator.mediaDevices.getUserMedia({ audio: STRICT_AUDIO });
    } catch {
      usedDefaultMic.value = true;
      return navigator.mediaDevices.getUserMedia({ audio: true });
    }
  }

  function stopTracks() {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      stream = null;
    }
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
    if (capTimer) {
      clearTimeout(capTimer);
      capTimer = null;
    }
  }

  async function start() {
    recorderError.value = '';
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      recorderError.value = 'This browser cannot record a visit. Import a voice memo instead, and leave this tab open while recording.';
      return false;
    }
    try {
      stream = await openStream();
    } catch {
      recorderError.value = 'Allow the microphone to record this visit.';
      return false;
    }

    const mime = pickMime();
    chunks = [];
    recorder = mime ? new MediaRecorder(stream, {
      mimeType: mime,
      audioBitsPerSecond: 128000,
    }) : new MediaRecorder(stream);
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size) chunks.push(event.data);
    };
    stopped = new Promise((resolve) => {
      recorder.onstop = () => {
        const type = recorder.mimeType || mime || 'audio/mp4';
        const blob = new Blob(chunks, { type });
        const file = new File([blob], `visit-${Date.now()}.${extensionFor(type)}`, { type });
        pendingFile = file;
        resolve(file);
      };
    });
    recorder.start(1000);
    recording.value = true;
    elapsedSec.value = 0;
    capped.value = false;
    pendingFile = null;
    timer = setInterval(() => {
      elapsedSec.value += 1;
    }, 1000);
    capTimer = setTimeout(() => {
      capped.value = true;
      if (recorder && recorder.state !== 'inactive') recorder.stop();
    }, MAX_SEC * 1000);
    return true;
  }

  async function stop() {
    if (pendingFile) {
      const file = pendingFile;
      pendingFile = null;
      recording.value = false;
      stopTracks();
      return file;
    }
    if (!recorder || recorder.state === 'inactive') {
      recording.value = false;
      stopTracks();
      return null;
    }
    recorder.stop();
    const file = await stopped;
    recording.value = false;
    stopTracks();
    recorder = null;
    return file;
  }

  function cancel() {
    chunks = [];
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = () => {};
      recorder.stop();
    }
    recorder = null;
    recording.value = false;
    elapsedSec.value = 0;
    stopTracks();
  }

  onUnmounted(cancel);

  return {
    recording,
    elapsedSec,
    recorderError,
    usedDefaultMic,
    capped,
    start,
    stop,
    cancel,
  };
}
